/**
 * useAutoTopUp
 *
 * Reads and writes the OLV wallet auto top-up settings doc at
 * `users/{uid}/wallet/autoTopUp` (users may write their own `users/{uid}/**`
 * per firestore.rules). Real-time via onSnapshot.
 *
 * Self-contained and NOT wired into any component — the settings UI consumes
 * `enable(opts)` / `disable()` / `update(opts)`.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
  Unsubscribe,
} from 'firebase/firestore';
import db from '../utils/firestore';
import {
  AutoTopUpSettings,
  AutoTopUpUpdate,
  AUTO_TOP_UP_DEFAULTS,
} from '../types/autoTopUp';

export interface UseAutoTopUpResult {
  /** Current settings (defaults until the doc loads / when absent). */
  settings: AutoTopUpSettings;
  /**
   * True once a settings doc exists in Firestore — i.e. the user has made an
   * explicit auto top-up choice at some point (enabled OR disabled). False for
   * never-configured users, which is what lets the top-up modal offer
   * default-on enrollment without ever overriding a stored "off".
   */
  hasStoredSettings: boolean;
  /** True while the initial snapshot is in flight. */
  isLoading: boolean;
  /** Non-null when the listener or a write failed. */
  error: string | null;
  /** Turn auto top-up on, optionally overriding any adjustable fields. */
  enable: (opts?: AutoTopUpUpdate) => Promise<void>;
  /** Turn auto top-up off (preserves the other settings). */
  disable: () => Promise<void>;
  /** Patch one or more adjustable fields without flipping `enabled`. */
  update: (opts: AutoTopUpUpdate) => Promise<void>;
}

function toNumber(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : fallback;
}

/** Coerce a raw Firestore doc into a fully-populated settings object. */
function normalize(data: any): AutoTopUpSettings {
  return {
    enabled: data?.enabled === true,
    thresholdUsd: toNumber(data?.thresholdUsd, AUTO_TOP_UP_DEFAULTS.thresholdUsd),
    topUpAmountUsd: toNumber(data?.topUpAmountUsd, AUTO_TOP_UP_DEFAULTS.topUpAmountUsd),
    maxChargesPer24h: Math.max(
      0,
      Math.floor(toNumber(data?.maxChargesPer24h, AUTO_TOP_UP_DEFAULTS.maxChargesPer24h)),
    ),
    paymentMethodId:
      typeof data?.paymentMethodId === 'string' && data.paymentMethodId
        ? data.paymentMethodId
        : undefined,
    updatedAt: data?.updatedAt,
  };
}

/** Keep only defined, well-typed adjustable fields for a merge write. */
function sanitize(opts?: AutoTopUpUpdate): Partial<AutoTopUpSettings> {
  const patch: Partial<AutoTopUpSettings> = {};
  if (!opts) return patch;
  if (opts.thresholdUsd !== undefined) {
    patch.thresholdUsd = Math.max(0, toNumber(opts.thresholdUsd, AUTO_TOP_UP_DEFAULTS.thresholdUsd));
  }
  if (opts.topUpAmountUsd !== undefined) {
    patch.topUpAmountUsd = Math.max(
      0,
      toNumber(opts.topUpAmountUsd, AUTO_TOP_UP_DEFAULTS.topUpAmountUsd),
    );
  }
  if (opts.maxChargesPer24h !== undefined) {
    patch.maxChargesPer24h = Math.max(
      0,
      Math.floor(toNumber(opts.maxChargesPer24h, AUTO_TOP_UP_DEFAULTS.maxChargesPer24h)),
    );
  }
  if (opts.paymentMethodId !== undefined) {
    patch.paymentMethodId = opts.paymentMethodId;
  }
  return patch;
}

export function useAutoTopUp(uid: string | null | undefined): UseAutoTopUpResult {
  const [settings, setSettings] = useState<AutoTopUpSettings>({ ...AUTO_TOP_UP_DEFAULTS });
  const [hasStoredSettings, setHasStoredSettings] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const unsubRef = useRef<Unsubscribe | null>(null);

  const path = uid ? `users/${uid}/wallet/autoTopUp` : null;

  useEffect(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    if (!path) {
      setSettings({ ...AUTO_TOP_UP_DEFAULTS });
      setHasStoredSettings(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    // try/catch: doc()/onSnapshot throw synchronously when the Firestore
    // client has been terminated (isolated test hosts, teardown races). Fail
    // open to the defaults — same contract as a listener error.
    try {
      unsubRef.current = onSnapshot(
        doc(db, path),
        (snap) => {
          setSettings(normalize(snap.exists() ? snap.data() : null));
          setHasStoredSettings(snap.exists());
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          setError(err.message || 'Failed to load auto top-up settings');
          setIsLoading(false);
        },
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to load auto top-up settings');
      setIsLoading(false);
    }
    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [path]);

  const write = useCallback(
    async (patch: Partial<AutoTopUpSettings>) => {
      if (!path) throw new Error('Cannot update auto top-up: not signed in');
      try {
        await setDoc(doc(db, path), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
      } catch (e: any) {
        setError(e?.message || 'Failed to update auto top-up settings');
        throw e;
      }
    },
    [path],
  );

  const enable = useCallback(
    (opts?: AutoTopUpUpdate) => write({ enabled: true, ...sanitize(opts) }),
    [write],
  );
  const disable = useCallback(() => write({ enabled: false }), [write]);
  const update = useCallback((opts: AutoTopUpUpdate) => write(sanitize(opts)), [write]);

  return { settings, hasStoredSettings, isLoading, error, enable, disable, update };
}

export default useAutoTopUp;
